import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { functions } from "../api/firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";

export interface Interview {
    id: string;
    userId: string;
    createdAt: {
        seconds: number;
        nanoseconds: number;
    };
    title?: string;
    caseId?: string;
    caseName?: string;
    caseProblemId?: number;
    status?: string;
    reportStatus?: string;
    feedback?: any;
    recording?: string;
    isActive?: boolean;
    isConsulting?: boolean;
    startTime?: {
        seconds: number;
        nanoseconds: number;
        _seconds?: number;
        _nanoseconds?: number;
    };
    endTime?: {
        seconds: number;
        nanoseconds: number;
        _seconds?: number;
        _nanoseconds?: number;
    };
    currentState?: {
        name?: string;
        phase?: string;
        context?: any;
    };
    contextSummary?: string;
    lastUpdated?: {
        seconds: number;
        nanoseconds: number;
    };
    [key: string]: any;
}

/**
 * Custom hook to fetch all interviews for the current user
 */
export function useFetchInterviews() {
    const { firebaseUser } = useAuth();
    const [error, setError] = useState<Error | null>(null);

    const fetchInterviews = async () => {
        try {
            if (!firebaseUser?.uid) {
                throw new Error("User not authenticated");
            }

            const getAllInterviews = httpsCallable(
                functions,
                "getAllInterviews"
            );
            const response = await getAllInterviews({
                userId: firebaseUser.uid,
            });

            // The cloud function returns data in the format { success: boolean, data: Interview[] }
            const result = response.data as {
                success: boolean;
                data: Interview[];
            };

            if (!result.success) {
                throw new Error("Failed to fetch interviews");
            }

            // Process interviews - convert any Firestore timestamp formats
            const processedInterviews = result.data.map((interview) => {
                // Handle Firestore timestamp format from cloud functions
                const processTimestamp = (timestamp: any) => {
                    if (!timestamp) return timestamp;

                    // Convert from Firestore format if needed
                    if (
                        timestamp._seconds !== undefined &&
                        timestamp._nanoseconds !== undefined
                    ) {
                        return {
                            seconds: timestamp._seconds,
                            nanoseconds: timestamp._nanoseconds,
                        };
                    }
                    return timestamp;
                };

                return {
                    ...interview,
                    createdAt: processTimestamp(interview.createdAt),
                    startTime: processTimestamp(interview.startTime),
                    endTime: processTimestamp(interview.endTime),
                    lastUpdated: processTimestamp(interview.lastUpdated),
                };
            });

            // Sort interviews by creation date (newest first)
            const sortedInterviews = processedInterviews.sort((a, b) => {
                const dateA = a.startTime?.seconds || a.createdAt?.seconds || 0;
                const dateB = b.startTime?.seconds || b.createdAt?.seconds || 0;
                return dateB - dateA;
            });
            // console.log("Sorted interviews:", sortedInterviews);
            return sortedInterviews;
        } catch (err) {
            console.error("Error fetching interviews:", err);
            setError(
                err instanceof Error ? err : new Error("Unknown error occurred")
            );
            return [];
        }
    };

    const {
        data: interviews,
        isLoading,
        refetch,
    } = useQuery<Interview[]>(
        ["interviews", firebaseUser?.uid],
        fetchInterviews,
        {
            enabled: !!firebaseUser?.uid,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            onError: (err) => {
                console.error("Query error:", err);
                setError(
                    err instanceof Error ? err : new Error("Query failed")
                );
            },
        }
    );

    return {
        interviews: interviews || [],
        isLoading,
        error,
        refetch,
    };
}

export default useFetchInterviews;
